import React, { useState, useEffect } from 'react';
import {
  Button,
  Col,
  Form,
  Modal,
  ModalFooter,
  ModalHeader,
  Row,
} from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { api } from '../../providers/apiClient';
import SubmitButton from '../form/submit-button';
import { CustomModalBody } from './styles';
import { formatDateToUTC, toUpperCaseClean } from '../../utils';

const getValidationSchema = (unitOptions, departmentOptions) =>
  Yup.object().shape({
    secretaria: Yup.string().required('Secretaria obrigatória'),
    unidade: Yup.string().optional(),
    departamento: Yup.string().optional(),
    resumo: Yup.string().required('Assunto obrigatório'),
    descricao: Yup.string().required('Descrição obrigatória'),
    prioridade: Yup.string().required('Prioridade obrigatória'),
    categoria: Yup.string().required('Categoria obrigatória'),
  });

const ModalNewTask = ({
  isModalOpened,
  handleToggleModal,
  fetchTasks,
  branchOptions,
  organization,
  user,
}) => {
  const [requesting, setRequesting] = useState(false);
  const [unitOptions, setUnitOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);

  const validationSchema = getValidationSchema(unitOptions, departmentOptions);

  const handleClose = (formik) => {
    formik.resetForm();
    handleToggleModal();
    setUnitOptions([]);
    setDepartmentOptions([]);
  };

  const handleSubmit = async (values, { resetForm }) => {
    setRequesting(true);

    // Encontrar os IDs corretos baseados nas seleções
    const selectedBranch = branchOptions.find(branch => 
      toUpperCaseClean(branch.name) === values.secretaria
    );
    const selectedUnit = unitOptions.find(unit => 
      toUpperCaseClean(unit.name) === values.unidade
    );
    const selectedDepartment = departmentOptions.find(dept => 
      toUpperCaseClean(dept.name) === values.departamento
    );

    const payload = {
      orgId: organization.id,
      branchId: selectedBranch?.id || null,
      unitId: selectedUnit?.id || null,
      departmentId: selectedDepartment?.id || null,
      usuarioId: user.id,
      telefone: user.phone ? `(${user.phone.ddd})${user.phone.number}` : null,
      resumo: values.resumo,
      descricao: values.descricao,
      observacao: values.observacao || null,
      prioridade: values.prioridade,
      categoria: values.categoria,
    };

    await api
      .post(`/service_desk`, payload)
      .then((response) => {
        toast.success('Chamado criado com sucesso!');
        handleToggleModal();
        resetForm();
        setUnitOptions([]);
        setDepartmentOptions([]);
        fetchTasks();
      })
      .catch((error) => {
        console.error('Erro ao criar chamado:', error);
        toast.error('Erro ao criar chamado.');
      })
      .finally(() => setRequesting(false));
  };

  async function fetchUnitSetup(branchId) {
    await api
      .get(`/branchs/${branchId}/unit_setup`)
      .then((r) => {
        setUnitOptions(r.data);
      })
      .catch(() => {})
      .finally(() => {});
  }

  async function fetchDepartmentSetup(unit_id) {
    await api
      .get(`/units/${unit_id}/department_setup`)
      .then((r) => {
        setDepartmentOptions(r.data);
      })
      .catch(() => {})
      .finally(() => {});
  }

  return (
    <Formik
      initialValues={{
        secretaria: '',
        unidade: '',
        departamento: '',
        resumo: '',
        descricao: '',
        observacao: '',
        prioridade: 'MEDIA',
        categoria: 'GERAL',
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <Modal
          id='modal-task'
          show={isModalOpened}
          centered
          onHide={() => handleClose(formik)}
          size='lg'
        >
          <form onSubmit={formik.handleSubmit}>
            <ModalHeader id='modal-task-header' closeButton>
              Novo Chamado
            </ModalHeader>
            <CustomModalBody id='modal-task-body'>
              <Row>
                <Col md={4}>
                  <Form.Group id='select-secretaria'>
                    <Form.Label>Secretaria:</Form.Label>
                    <Form.Select
                      {...formik.getFieldProps('secretaria')}
                      isInvalid={
                        formik.touched.secretaria && formik.errors.secretaria
                      }
                      onChange={(event) => {
                        const selectedOption =
                          event.target.options[event.target.selectedIndex];
                        const branchId = selectedOption.getAttribute('data-id');
                        setUnitOptions([]);
                        setDepartmentOptions([]);
                        formik.setFieldError('unidade', '');
                        formik.setFieldTouched('unidade', false);
                        formik.setFieldValue('unidade', '');
                        formik.setFieldError('departamento', '');
                        formik.setFieldTouched('departamento', false);
                        formik.setFieldValue('departamento', '');
                        if (branchId) {
                          fetchUnitSetup(branchId);
                        }
                        formik.handleChange(event);
                      }}
                    >
                      <option value=''>Selecione...</option>
                      {branchOptions.map((option) => (
                        <option
                          key={option.id}
                          value={toUpperCaseClean(option.name)}
                          data-id={option.id}
                        >
                          {option.name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type='invalid'>
                      {formik.errors.secretaria}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group id='select-unidade'>
                    <Form.Label>Unidade:</Form.Label>
                    <Form.Select
                      disabled={unitOptions.length === 0}
                      {...formik.getFieldProps('unidade')}
                      isInvalid={
                        formik.touched.unidade && formik.errors.unidade
                      }
                      onChange={(event) => {
                        const selectedOption =
                          event.target.options[event.target.selectedIndex];
                        const unit_id = selectedOption.getAttribute('data-id');
                        setDepartmentOptions([]);
                        formik.setFieldError('departamento', '');
                        formik.setFieldTouched('departamento', false);
                        formik.setFieldValue('departamento', '');
                        if (unit_id) {
                          fetchDepartmentSetup(unit_id);
                        }
                        formik.handleChange(event);
                      }}
                    >
                      <option value=''>Selecione...</option>
                      {unitOptions.map((option) => (
                        <option
                          key={option.id}
                          value={toUpperCaseClean(option.name)}
                          data-id={option.id}
                        >
                          {option.name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type='invalid'>
                      {formik.errors.unidade}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group id='select-departamento'>
                    <Form.Label>Departamento:</Form.Label>
                    <Form.Select
                      disabled={departmentOptions.length === 0}
                      {...formik.getFieldProps('departamento')}
                      isInvalid={
                        formik.touched.departamento &&
                        formik.errors.departamento
                      }
                    >
                      <option value=''>Selecione...</option>
                      {departmentOptions.map((option) => (
                        <option
                          key={option.id}
                          value={toUpperCaseClean(option.name)}
                        >
                          {option.name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type='invalid'>
                      {formik.errors.departamento}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group>
                <Form.Label>Assunto</Form.Label>
                <Form.Control
                  as='textarea'
                  rows='1'
                  style={{ caretColor: 'auto', resize: 'none' }}
                  {...formik.getFieldProps('resumo')}
                  isInvalid={formik.touched.resumo && formik.errors.resumo}
                />
                <Form.Control.Feedback type='invalid'>
                  {formik.errors.resumo}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group>
                <Form.Label>Descrição</Form.Label>
                <Form.Control
                  style={{ caretColor: 'auto', resize: 'none' }}
                  rows='4'
                  as='textarea'
                  {...formik.getFieldProps('descricao')}
                  isInvalid={
                    formik.touched.descricao && formik.errors.descricao
                  }
                />
                <Form.Control.Feedback type='invalid'>
                  {formik.errors.descricao}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group>
                <Form.Label>Observação</Form.Label>
                <Form.Control
                  style={{ caretColor: 'auto', resize: 'none' }}
                  rows='2'
                  as='textarea'
                  {...formik.getFieldProps('observacao')}
                  isInvalid={
                    formik.touched.observacao && formik.errors.observacao
                  }
                />
                <Form.Control.Feedback type='invalid'>
                  {formik.errors.observacao}
                </Form.Control.Feedback>
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group id='select-prioridade'>
                    <Form.Label>Prioridade:</Form.Label>
                    <Form.Select
                      {...formik.getFieldProps('prioridade')}
                      isInvalid={
                        formik.touched.prioridade && formik.errors.prioridade
                      }
                    >
                      <option value='BAIXA'>Baixa</option>
                      <option value='MEDIA'>Média</option>
                      <option value='ALTA'>Alta</option>
                    </Form.Select>
                    <Form.Control.Feedback type='invalid'>
                      {formik.errors.prioridade}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group id='select-categoria'>
                    <Form.Label>Categoria:</Form.Label>
                    <Form.Select
                      {...formik.getFieldProps('categoria')}
                      isInvalid={
                        formik.touched.categoria && formik.errors.categoria
                      }
                    >
                      <option value='GERAL'>Geral</option>
                      <option value='TECNICO'>Técnico</option>
                      <option value='SUPORTE'>Suporte</option>
                      <option value='MANUTENCAO'>Manutenção</option>
                      <option value='OUTROS'>Outros</option>
                    </Form.Select>
                    <Form.Control.Feedback type='invalid'>
                      {formik.errors.categoria}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </CustomModalBody>
            <ModalFooter id='modal-task-footer'>
              <Button
                type='button'
                variant='light'
                onClick={() => handleClose(formik)}
                id='modal-task-cancel-btn'
              >
                Cancelar
              </Button>
              <SubmitButton type='submit' loading={requesting} block={false} id='modal-task-save-btn'>
                Salvar
              </SubmitButton>
            </ModalFooter>
          </form>
        </Modal>
      )}
    </Formik>
  );
};

export default ModalNewTask;
